import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getProfile } from '../../store/auth/actions';
import { getOperationStatus, redeemLaunchToken } from './handoffApi';
import {
	MIN_LOADING_MS,
	MIN_LOADING_MS_ON_FAILURE,
	POLL_BACKOFF_FACTOR,
	POLL_BUDGET_MS,
	POLL_INTERVAL_MS,
	POLL_MAX_INTERVAL_MS
} from './handoffConstants';
import { readAndClearLaunchToken, sleep } from './handoffUtils';

const POLL_TIMEOUT = 'TIMEOUT';

/**
 * Poll the operation status within the FE budget, backing off between reads.
 * Resolves with the terminal status body, or `'TIMEOUT'` if the budget runs out.
 */
const pollStatus = async (operationId, sessionToken) => {
	const deadline = Date.now() + POLL_BUDGET_MS;
	let wait = POLL_INTERVAL_MS;

	while (Date.now() < deadline) {
		const body = await getOperationStatus(operationId, sessionToken);
		if (body.status === 'PROCESSED' || body.status === 'FAILED') return body;

		await sleep(Math.min(wait, deadline - Date.now()));
		wait = Math.min(wait * POLL_BACKOFF_FACTOR, POLL_MAX_INTERVAL_MS);
	}

	return POLL_TIMEOUT;
};

/**
 * Orchestrates the whole handoff landing flow and exposes which screen to render.
 * Enters the app by reusing the existing login machinery.
 */
export const useHandoff = () => {
	const [screen, setScreen] = useState('loading');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const hasRun = useRef(false);

	useEffect(() => {
		// The launch token is single-use, so redeeming twice would fail the second
		// attempt: the flow must run once per mount, whatever re-renders happen.
		if (hasRun.current) return;
		hasRun.current = true;

		const run = async () => {
			const startedAt = Date.now();
			// Keep the loading screen up for at least `minMs` before any transition,
			// so a fast operation doesn't make it flash. It's a floor, not an added
			// delay: if the flow already took longer, there's no extra wait.
			const finish = async (minMs, apply) => {
				const elapsed = Date.now() - startedAt;
				if (elapsed < minMs) await sleep(minMs - elapsed);
				apply();
			};
			// Failures get a longer floor than the happy path: even when we already
			// know it failed (e.g. an immediate 401), bouncing to the email screen
			// instantly reads worse than a slightly longer loading screen.
			const finishWithFailure = (apply) => finish(MIN_LOADING_MS_ON_FAILURE, apply);

			const launchToken = readAndClearLaunchToken();
			if (!launchToken) return finishWithFailure(() => setScreen('email'));

			let session;
			try {
				session = await redeemLaunchToken(launchToken);
			} catch (error) {
				return finishWithFailure(() => setScreen('email'));
			}

			let result;
			try {
				result = await pollStatus(session.operationId, session.sessionToken);
			} catch (error) {
				return finishWithFailure(() => setScreen('email'));
			}

			if (result === POLL_TIMEOUT || result.status === 'FAILED') {
				return finishWithFailure(() => setScreen('email'));
			}

			if (result.userPreexisted === false && result.accessToken) {
				// New user: auto-login, no intermediate screen. `getProfile` stores the
				// session and flips `isAuthenticated`, and from there `navigation.js`
				// sends the user to `/distributor` on its own, because this route is a
				// public one and an authenticated visitor no longer belongs on it.
				return finish(MIN_LOADING_MS, () => {
					dispatch(getProfile({ token: result.accessToken, navigate }));
				});
			}

			// Anything else means the account already exists (a duplicate lead, or a
			// known user without an active operation), so the only way in is the
			// login. A missing `accessToken` on a new user lands here too: of the two
			// terminal screens it's the only one that lets the user carry on alone.
			return finish(MIN_LOADING_MS, () => setScreen('login'));
		};

		run();
	}, [dispatch, navigate]);

	return { screen };
};
